import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Drawer } from '@mui/material';
import { DragDropContext, Draggable, DropResult, Droppable } from 'react-beautiful-dnd';
import { Controller, FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';

import { useProjectTreeStore } from 'store/projectContract.store';

import { GET_POAP_VIEW_BY_ID_QUERY_KEY } from 'api/queries/getPoapViewById.query';
import { PROGRESS_MEASUREMENT_METHODS_KEYS } from 'api/queries/getProgressMeasurementMethods.query';

import { CloseButton } from 'components/common/CloseButton/CloseButton';
import { TooltipPosition } from 'components/common/CustomizedTooltip/customizedTooltip.enums';
import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';

import { Alert } from 'components/DesignSystem/Alert';
import { Button } from 'components/DesignSystem/Button';
import { Modal } from 'components/DesignSystem/Modal';
import { Select } from 'components/DesignSystem/Select';
import { CustomizedTooltip } from 'components/common';
import { UseViewsManagerProvider } from 'components/pages/ScheduleViewsPage/ViewsManager/ViewsManager.provider';
import { ReadOnlyTooltipContent } from 'components/pages/ScheduleViewsPage/components/ReadOnlyTooltipContent/ReadOnlyTooltipContent';
import { ViewRowsFormValues } from 'components/pages/ScheduleViewsPage/components/ViewConfiguration/ViewConfiguration.types';
import classes from 'components/pages/ScheduleViewsPage/components/ViewConfiguration/components/ViewConfigurationDrawerForm/ViewConfigurationDrawerForm.module.scss';
import { FloatTooltip } from 'components/pages/ScheduleViewsPage/components/ViewConfiguration/components/ViewConfigurationDrawerForm/components/FloatTooltip/FloatTooltip';
import { NoRowsConfiguredEmptyState } from 'components/pages/ScheduleViewsPage/components/ViewConfiguration/components/ViewConfigurationDrawerForm/components/NoRowsConfiguredEmptyState/NoRowsConfiguredEmptyState';
import { RowConfig } from 'components/pages/ScheduleViewsPage/components/ViewConfiguration/components/ViewConfigurationDrawerForm/components/RowConfig/RowConfig';
import {
    RowConfigItem,
    RowConfigView,
} from 'components/pages/ScheduleViewsPage/components/ViewConfiguration/components/ViewConfigurationDrawerForm/components/RowConfigView/RowConfigView';
import {
    buildProgressMeasurementFormValues,
    useGetRows,
} from 'components/pages/ScheduleViewsPage/components/ViewConfiguration/viewConfigurationUtils';

const PARTIAL_OPTIONS: ISelectOption<string>[] = [
    { label: 'Yes', value: 'true' },
    { label: 'No', value: 'false' },
];

const PROGRESS_MEASUREMENT_TOOLTIP =
    'Choose how progress is measured for all rows in this view (e.g. criticality-weighted vs resource units).';

const PARTIAL_PROGRESS_TOOLTIP =
    'When enabled, activities that are only partially complete count toward progress. Not applicable for resource-based measurement.';

const DISCARD_CHANGES_TITLE = 'Discard unsaved changes?';
const DISCARD_CHANGES_MESSAGE =
    'You have unsaved changes. Close without saving, or stay on this screen to keep editing.';

function getDefaultRowData(): RowConfigItem['data'] {
    const project = useProjectTreeStore.getState().project;
    const contract = useProjectTreeStore.getState().contract;
    return {
        project: project ?? undefined,
        contract: contract ?? undefined,
        rowName: '',
        rowType: undefined,
        filterItem: undefined,
        filterItemReferences: [],
        latestVersionId: undefined,
        compareVersionId: undefined,
        rowId: undefined,
    };
}

interface Props {
    api: UseViewsManagerProvider['api'];
    isOpen: boolean;
    onClose: () => void;
    viewId: number;
    isViewOwner: boolean;
}

export const ViewConfigurationDrawerForm = ({ api, isOpen, onClose, viewId, isViewOwner }: Props) => {
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [isAddingNew, setIsAddingNew] = useState(false);
    const [isDiscardConfirmOpen, setIsDiscardConfirmOpen] = useState(false);
    const queryClient = useQueryClient();
    const rowSnapshotBeforeEditRef = useRef<RowConfigItem | null>(null);

    const { rows: apiRows, savedProgressFromFirstRow } = useGetRows({ api, viewId });

    const form = useForm<ViewRowsFormValues>({
        defaultValues: {
            rows: [],
            progressMeasurementCode: PROGRESS_MEASUREMENT_METHODS_KEYS.CRITICALITY_WEIGHT,
            progressMeasurementPartial: false,
        },
    });
    const { fields, append, remove, move } = useFieldArray({
        control: form.control,
        name: 'rows',
    });
    const rows = form.watch('rows');
    const {
        formState: { isDirty },
        reset,
        watch,
        setValue,
        getValues,
        control,
    } = form;

    const startEditingRow = useCallback(
        (index: number) => {
            rowSnapshotBeforeEditRef.current = structuredClone(getValues(`rows.${index}`) as RowConfigItem);
            setIsAddingNew(false);
            setEditingIndex(index);
        },
        [getValues],
    );

    const progressMeasurementMethods = api.getProgressMeasurementMethods.instance.data;
    const isProgressMeasurementMethodsLoading = api.getProgressMeasurementMethods.instance.isLoading;

    const progressCodeOptions: ISelectOption<string>[] = useMemo(() => {
        return (
            progressMeasurementMethods?.map((method) => ({
                label: method.value,
                value: method.key,
            })) ?? []
        );
    }, [progressMeasurementMethods]);

    const progressMeasurementCode = watch('progressMeasurementCode');

    const isResourceUnitsSelected = progressMeasurementCode === PROGRESS_MEASUREMENT_METHODS_KEYS.RESOURCE_UNITS;

    useEffect(() => {
        if (!isResourceUnitsSelected) return;
        if (getValues('progressMeasurementPartial')) {
            setValue('progressMeasurementPartial', false, { shouldDirty: true });
        }
    }, [isResourceUnitsSelected, getValues, setValue]);

    const isFormOpen = isAddingNew || editingIndex !== null;
    const showAddRowFloatHint = isViewOwner && !isFormOpen && fields.length === 0;

    useEffect(() => {
        if (isOpen) {
            setIsDiscardConfirmOpen(false);
            reset({
                rows: apiRows,
                ...buildProgressMeasurementFormValues(progressMeasurementMethods, savedProgressFromFirstRow),
            });
        }
    }, [isOpen, reset, apiRows, savedProgressFromFirstRow, progressMeasurementMethods]);

    const handleCloseDrawer = useCallback(() => {
        rowSnapshotBeforeEditRef.current = null;
        reset({
            rows: apiRows,
            ...buildProgressMeasurementFormValues(progressMeasurementMethods, savedProgressFromFirstRow),
        });
        onClose();
        setEditingIndex(null);
        setIsAddingNew(false);
        setIsDiscardConfirmOpen(false);
    }, [reset, apiRows, savedProgressFromFirstRow, progressMeasurementMethods, onClose]);

    const requestCloseDrawer = useCallback(() => {
        if (isDirty) {
            setIsDiscardConfirmOpen(true);
            return;
        }
        handleCloseDrawer();
    }, [isDirty, handleCloseDrawer]);

    const confirmDiscardAndCloseDrawer = useCallback(() => {
        setIsDiscardConfirmOpen(false);
        handleCloseDrawer();
    }, [handleCloseDrawer]);

    const handleAddNewRow = useCallback(() => {
        rowSnapshotBeforeEditRef.current = null;
        append({ data: getDefaultRowData() });
        setEditingIndex(fields.length);
        setIsAddingNew(true);
    }, [append, fields.length]);

    const handleConfirmAdd = useCallback(() => {
        rowSnapshotBeforeEditRef.current = null;
        setIsAddingNew(false);
        setEditingIndex(null);
    }, []);

    const handleSaveRow = useCallback(() => {
        rowSnapshotBeforeEditRef.current = null;
        setEditingIndex(null);
    }, []);

    const handleCancelForm = useCallback(() => {
        if (editingIndex === null) return;

        if (isAddingNew) {
            remove(editingIndex);
        } else if (rowSnapshotBeforeEditRef.current) {
            setValue(`rows.${editingIndex}`, rowSnapshotBeforeEditRef.current, {
                shouldDirty: false,
                shouldTouch: false,
                shouldValidate: false,
            });
        }

        rowSnapshotBeforeEditRef.current = null;
        setIsAddingNew(false);
        setEditingIndex(null);
    }, [editingIndex, isAddingNew, remove, setValue]);

    const handleDeleteRow = useCallback(
        (index: number) => {
            rowSnapshotBeforeEditRef.current = null;
            remove(index);
            setEditingIndex(null);
        },
        [remove],
    );

    const handleDragEnd = useCallback(
        (result: DropResult) => {
            if (!result.destination) return;
            const from = result.source.index;
            const to = result.destination.index;
            if (from === to) return;
            move(from, to);
        },
        [move],
    );

    const handleSaveAndClose = useCallback(
        (data: ViewRowsFormValues) => {
            const partial =
                data.progressMeasurementCode === PROGRESS_MEASUREMENT_METHODS_KEYS.RESOURCE_UNITS
                    ? false
                    : data.progressMeasurementPartial;
            const progressMeasurement = {
                code: data.progressMeasurementCode,
                partial,
            };

            const viewRows = data.rows.map((row: RowConfigItem, index) => {
                const filterItemReferences = row.data.filterItemReferences?.map((ref) => ref.value) ?? [];
                return {
                    ...(row.data.rowId != null && { rowId: row.data.rowId }),
                    rowName: row.data.rowName,
                    projectId: row.data.project?.id ?? 0,
                    contractId: row.data.contract?.id ?? 0,
                    latestVersionId: row.data.latestVersionId ?? 0,
                    compareVersionId: row.data.compareVersionId ?? 0,
                    rowType: row.data.rowType,
                    filterItem: row.data.filterItem,
                    filterItemReferences: filterItemReferences,
                    rowOrder: index,
                    progressMeasurement,
                };
            });

            const payload = {
                viewId,
                viewRows,
            };
            api.updatePoapView.instance.mutate(payload, {
                onSuccess: () => {
                    queryClient.invalidateQueries([GET_POAP_VIEW_BY_ID_QUERY_KEY, { viewId }]);
                },
            });
            rowSnapshotBeforeEditRef.current = null;
            onClose();
            setEditingIndex(null);
            setIsAddingNew(false);
        },
        [api, viewId, queryClient, onClose],
    );

    return (
        <>
            <Drawer
                anchor={'right'}
                open={isOpen}
                onClose={requestCloseDrawer}
                PaperProps={{
                    sx: {
                        backgroundColor: 'var(--bg-primary)',
                        width: 650,
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        maxHeight: '100vh',
                    },
                }}
            >
                <FormProvider {...form}>
                    <form onSubmit={form.handleSubmit(handleSaveAndClose)} className={classes.drawerForm}>
                        <div className={classes.drawerWrapper}>
                            <div className={classes.drawerHeader}>
                                <div className={classes.drawerTitleRow}>
                                    <MuiIcon
                                        icon={'tune'}
                                        type={'outlined'}
                                        fontSize={'24px'}
                                        color={'var(--text-primary)'}
                                    />
                                    <span className={classes.drawerTitle}>Configure View Rows</span>
                                </div>
                                <CloseButton onClick={requestCloseDrawer} />
                            </div>
                            <div className={classes.drawerContent}>
                                {isViewOwner && (
                                    <Alert className={classes.drawerDescription} severity={'info'}>
                                        Define the rows shown in the timeline. Drag to reorder rows, edit how they are
                                        presented, and select the activities powering them.
                                    </Alert>
                                )}

                                <div className={classes.progressMeasurementRow}>
                                    <div className={classes.progressMeasurementField}>
                                        <div className={classes.labelWithInfo}>
                                            <label htmlFor={'view-progress-measurement-code'}>
                                                Progress measurement by:
                                            </label>
                                            <CustomizedTooltip
                                                tooltipContent={PROGRESS_MEASUREMENT_TOOLTIP}
                                                triggerElement={
                                                    <button
                                                        type={'button'}
                                                        className={classes.infoIconButton}
                                                        aria-label={'Progress measurement info'}
                                                    >
                                                        <MuiIcon
                                                            icon={'info'}
                                                            type={'outlined'}
                                                            fontSize={'2.2rem'}
                                                            color={'var(--text-brand-primary)'}
                                                        />
                                                    </button>
                                                }
                                            />
                                        </div>
                                        <Controller
                                            name={'progressMeasurementCode'}
                                            control={control}
                                            render={({ field }) => (
                                                <CustomizedTooltip
                                                    tooltipPosition={TooltipPosition.Bottom}
                                                    disabled={isViewOwner}
                                                    tooltipContent={<ReadOnlyTooltipContent />}
                                                    triggerElement={
                                                        <Select<ISelectOption<string>>
                                                            {...field}
                                                            data-testid={'view-config-drawer-progress-measurement'}
                                                            id={'view-progress-measurement-code'}
                                                            className={classes.selectFullWidth}
                                                            options={progressCodeOptions}
                                                            value={
                                                                progressCodeOptions.find(
                                                                    (o) => o.value === field.value,
                                                                ) || null
                                                            }
                                                            onChange={(newValue) => {
                                                                const next = newValue?.value ?? '';
                                                                field.onChange(next);
                                                                if (
                                                                    next ===
                                                                    PROGRESS_MEASUREMENT_METHODS_KEYS.RESOURCE_UNITS
                                                                ) {
                                                                    setValue('progressMeasurementPartial', false, {
                                                                        shouldDirty: true,
                                                                    });
                                                                }
                                                            }}
                                                            isLoading={isProgressMeasurementMethodsLoading}
                                                            isDisabled={isFormOpen || !isViewOwner}
                                                        />
                                                    }
                                                />
                                            )}
                                        />
                                    </div>
                                    <div className={classes.progressMeasurementField}>
                                        <div className={classes.labelWithInfo}>
                                            <label htmlFor={'view-progress-measurement-partial'}>
                                                Include partially progressed activities
                                            </label>
                                            <CustomizedTooltip
                                                tooltipContent={PARTIAL_PROGRESS_TOOLTIP}
                                                triggerElement={
                                                    <button
                                                        type={'button'}
                                                        className={classes.infoIconButton}
                                                        aria-label={'Partial progress info'}
                                                    >
                                                        <MuiIcon
                                                            icon={'info'}
                                                            type={'outlined'}
                                                            fontSize={'2.2rem'}
                                                            color={'var(--text-brand-primary)'}
                                                        />
                                                    </button>
                                                }
                                            />
                                        </div>
                                        <Controller
                                            name={'progressMeasurementPartial'}
                                            control={control}
                                            render={({ field }) => (
                                                <CustomizedTooltip
                                                    tooltipPosition={TooltipPosition.Bottom}
                                                    disabled={isViewOwner}
                                                    tooltipContent={<ReadOnlyTooltipContent />}
                                                    triggerElement={
                                                        <Select<ISelectOption<string>>
                                                            {...field}
                                                            data-testid={'view-config-drawer-partial-progress'}
                                                            id={'view-progress-measurement-partial'}
                                                            className={classes.selectFullWidth}
                                                            options={PARTIAL_OPTIONS}
                                                            value={
                                                                PARTIAL_OPTIONS.find(
                                                                    (o) => o.value === String(field.value),
                                                                ) ?? null
                                                            }
                                                            onChange={(newValue) => {
                                                                field.onChange(
                                                                    newValue ? newValue.value === 'true' : false,
                                                                );
                                                            }}
                                                            isDisabled={
                                                                isFormOpen || isResourceUnitsSelected || !isViewOwner
                                                            }
                                                        />
                                                    }
                                                />
                                            )}
                                        />
                                    </div>
                                </div>
                                <div className={classes.rowsScrollArea}>
                                    <div
                                        className={`${classes.rowsSection}${
                                            fields.length === 0
                                                ? ` ${classes.rowsSectionEmpty}`
                                                : ` ${classes.rowsSectionHasRows}`
                                        }`}
                                    >
                                        {fields.length === 0 ? (
                                            <NoRowsConfiguredEmptyState isViewOwner={isViewOwner} />
                                        ) : (
                                            <DragDropContext onDragEnd={handleDragEnd}>
                                                <Droppable droppableId={'view-rows-list'}>
                                                    {(droppableProvided) => (
                                                        <div
                                                            ref={droppableProvided.innerRef}
                                                            {...droppableProvided.droppableProps}
                                                            className={classes.rowsList}
                                                        >
                                                            {fields.map((field, index) => {
                                                                const row = rows[index];
                                                                if (!row) return null;
                                                                const isEditing = index === editingIndex;
                                                                return (
                                                                    <Draggable
                                                                        key={field.id}
                                                                        draggableId={field.id}
                                                                        index={index}
                                                                        isDragDisabled={isFormOpen || !isViewOwner}
                                                                    >
                                                                        {(provided, snapshot) =>
                                                                            isEditing ? (
                                                                                <div
                                                                                    ref={provided.innerRef}
                                                                                    {...provided.draggableProps}
                                                                                    className={`${classes.rowConfigWrapper} ${classes.rowConfigWrapperActive}`}
                                                                                >
                                                                                    <RowConfig
                                                                                        api={api}
                                                                                        mode={
                                                                                            isAddingNew ? 'add' : 'edit'
                                                                                        }
                                                                                        rowIndex={index}
                                                                                        onCancel={handleCancelForm}
                                                                                        onConfirm={
                                                                                            isAddingNew
                                                                                                ? handleConfirmAdd
                                                                                                : handleSaveRow
                                                                                        }
                                                                                        onDelete={() =>
                                                                                            handleDeleteRow(index)
                                                                                        }
                                                                                    />
                                                                                </div>
                                                                            ) : (
                                                                                <RowConfigView
                                                                                    row={row}
                                                                                    onEdit={() =>
                                                                                        startEditingRow(index)
                                                                                    }
                                                                                    onDelete={() =>
                                                                                        handleDeleteRow(index)
                                                                                    }
                                                                                    provided={provided}
                                                                                    isEditDisabled={isFormOpen}
                                                                                    isViewOwner={isViewOwner}
                                                                                    isDragging={snapshot.isDragging}
                                                                                />
                                                                            )
                                                                        }
                                                                    </Draggable>
                                                                );
                                                            })}
                                                            {droppableProvided.placeholder}
                                                        </div>
                                                    )}
                                                </Droppable>
                                            </DragDropContext>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className={classes.drawerFooter}>
                                <CustomizedTooltip
                                    tooltipContent={<ReadOnlyTooltipContent />}
                                    disabled={isViewOwner}
                                    triggerElement={
                                        <FloatTooltip label={'Click here to start'} visible={showAddRowFloatHint}>
                                            <Button
                                                type={'button'}
                                                variant={'secondary'}
                                                onClick={handleAddNewRow}
                                                disabled={isFormOpen || !isViewOwner}
                                                data-testid={'view-config-drawer-add-row'}
                                                startIcon={<MuiIcon icon={'add'} />}
                                                className={classes.actionButton}
                                            >
                                                Add new row
                                            </Button>
                                        </FloatTooltip>
                                    }
                                />
                                <CustomizedTooltip
                                    tooltipContent={<ReadOnlyTooltipContent />}
                                    disabled={isViewOwner}
                                    triggerElement={
                                        <Button
                                            type={'submit'}
                                            variant={'primary'}
                                            disabled={!isDirty || isFormOpen || !isViewOwner}
                                            data-testid={'view-config-drawer-save-and-close'}
                                            className={classes.actionButton}
                                        >
                                            Save and close
                                        </Button>
                                    }
                                />
                            </div>
                        </div>
                    </form>
                </FormProvider>
            </Drawer>

            <Modal
                open={isDiscardConfirmOpen}
                onClose={() => setIsDiscardConfirmOpen(false)}
                title={DISCARD_CHANGES_TITLE}
                actions={
                    <>
                        <Button type={'button'} variant={'secondary'} onClick={confirmDiscardAndCloseDrawer}>
                            Discard and close
                        </Button>
                        <Button type={'button'} variant={'primary'} onClick={() => setIsDiscardConfirmOpen(false)}>
                            Cancel
                        </Button>
                    </>
                }
            >
                <p>{DISCARD_CHANGES_MESSAGE}</p>
            </Modal>
        </>
    );
};
